import React, { useEffect, useState, useRef, useMemo, useCallback } from "react";
import "./PropertyDetails.css";
import myImage from "../../../assets/images/house.png";
import trash from "../../../assets/images/trash.svg";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { openModal } from "../../../redux/modal/modalActions";
import { routeNames } from "../../../consts/routesNames";
import TextField from "../../../components/common/textfield/TextField";
import Button from "../../../components/common/button/Button";
import ProjectProcessAccordion from "../../../components/common/projectProcessAccordion/ProjectProcessAccordion";
import PropertySummaryAccordions from "../../../components/common/propertySummaryAccordions/PropertySummaryAccordions";
import Carousel from "../../../components/common/carousel/ItemsCarousel";
import ResponseModal from "../../../components/common/modal/responseModal/ResponseModal";
import { getProcessPropertyStatusById } from "../../../apis/processesApis/getProcessPropertyStatusById";
import { updateProcessPropertyStatuses } from "../../../apis/processesApis/updateProcessPropertyStatuses";
import { getAllContacts } from "../../../apis/contactsApis/getAllContacts";
import { getAllCompanies } from "../../../apis/companiesApis/getAllCompanies";
import { assignPropertyContact } from "../../../apis/propertiesApis/assignPropertyContact";
import { unassignPropertyContact } from "../../../apis/propertiesApis/unassignPropertyContact";
import { assignPropertyCompany } from "../../../apis/propertiesApis/assignPropertyCompany";
import { unassignPropertyCompany } from "../../../apis/propertiesApis/unassignPropertyCompany";
import { getPropertyDetails } from "../../../apis/propertiesApis/getPropertyDetails";
import { deletePropertyImages, updatePropertyImages, updatePropertyNotes } from "../../../redux/projects/projectsAction";
import { propertyImagesSelector } from "../../../redux/projects/projectSelector";
import { getCookie } from "../../../utils/cookies";
import CircularProgress from "@mui/material/CircularProgress";
import PropertyTasksSection from "../../../components/propertyTasks/PropertyTasksSection";
import { usePropertyDetailsPermissions } from "../../../auth/hooks/usePropertyDetailsPermissions";
import { useAuth } from "../../../auth/hooks/usePermissions";
import { useUpdateOwnership } from "../../../auth/hooks/useUpdateOwnership";
import { isAdminRole } from "../../../auth/utils/roles";
import TeamMembersTable from "./teamMembers/TeamMembersTable";
import {
  buildCompanyTeamMemberColumns,
  buildContactTeamMemberColumns,
  mapCompanyTeamMemberRow,
  mapContactTeamMemberRow,
} from "./teamMembers/TeamMembersTableColumns";

function PropertyDetails() {
  const location = useLocation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  /** Ignores stale responses when switching projects before the request finishes */
  const processStatusFetchSeq = useRef(0);
  const {
    canViewContactsSection,
    canViewCompaniesSection,
    canViewPropertyImagesSection,
    canUploadPropertyImages,
    canDeletePropertyImages,
    canUpdatePropertyNotes,
    canAssignContacts,
    canUnassignContacts,
    canAssignCompanies,
    canUnassignCompanies,
    canViewProcessStatuses,
    canUpdateProcessStatuses,
    canViewTasks,
    canCreateTasks,
    canUpdateTasks,
    canDeleteTasks,
  } = usePropertyDetailsPermissions();
  const { canUpdateOwnedRecord } = useUpdateOwnership();
  const { user } = useAuth();
  const isAdminUser = isAdminRole(user?.role);

  const propertyImages = useSelector(propertyImagesSelector);
  const reduxSelectedPropertyId = useSelector((state) => state.property.selectedpropertyId);
  const stateRow = location.state?.data;
  const resolvedPropertyId = useMemo(() => {
    const raw = searchParams.get("id");
    const parsed = raw != null && raw !== "" ? parseInt(raw, 10) : NaN;
    if (!Number.isNaN(parsed) && parsed > 0) return parsed;
    return stateRow?.id;
  }, [searchParams, stateRow]);

  /** Always loaded via getPropertyDetails — do not seed from navigation state (task rows only carry id/address). */
  const [propertyData, setPropertyData] = useState(null);
  const [processesData, setProcessesData] = useState([]);
  const [statusesData, setStatusesData] = useState([]);
  const [images, setImages] = useState([]);
  const [profileImage, setProfileImage] = useState([]);
  const [notes, setNotes] = useState("");
  const [allContacts, setAllContacts] = useState([]);
  const [allCompanies, setAllCompanies] = useState([]);
  const [assignedContactIds, setAssignedContactIds] = useState(new Set());
  const [assignedCompanyIds, setAssignedCompanyIds] = useState(new Set());
  const [contactSearch, setContactSearch] = useState("");
  const [companySearch, setCompanySearch] = useState("");
  const [contactsLoading, setContactsLoading] = useState(false);
  const [companiesLoading, setCompaniesLoading] = useState(false);
  const [processStatusLoading, setProcessStatusLoading] = useState(false);

  const canModifyPropertyNotes = canUpdateOwnedRecord(propertyData?.created_by, canUpdatePropertyNotes);
  const canModifyProcessStatuses = canUpdateOwnedRecord(propertyData?.created_by, canUpdateProcessStatuses);
  const canModifyPropertyImages = canUploadPropertyImages;
  const canModifyPropertyImageDeletes = canDeletePropertyImages;

  const initializeStatusesData = useCallback((processesGrouped, propertyIdForStatus) => {
    const initialStatuses = [];
    const phases = Array.isArray(processesGrouped) ? processesGrouped : [];
    phases.forEach(({ subPhases }) => {
      (subPhases || []).forEach((subPhase) => {
        initialStatuses.push({
          property_id: propertyIdForStatus,
          process_property_status_id: subPhase.process_id,
          is_completed: subPhase.is_completed,
        });
      });
    });
    setStatusesData(initialStatuses);
  }, []);

  /** Sort key: backend order field, else id, else stable large sentinel */
  const phaseSortKey = useCallback((item) => item?.process?.phase?.order ?? item?.process?.phase?.id ?? Number.MAX_SAFE_INTEGER, []);
  const subPhaseSortKey = useCallback(
    (item) => item?.process?.sub_phase?.order ?? item?.process?.sub_phase?.id ?? Number.MAX_SAFE_INTEGER,
    [],
  );

  /**
   * Returns phases in a stable order (array, not a plain object).
   * Plain objects reorder integer-like keys (e.g. "1","2","10"), which broke accordion order.
   */
  const groupByPhase = useCallback(
    (items) => {
      let list = items;
      if (list == null) return [];
      if (typeof list === "object" && !Array.isArray(list)) {
        list = list.data ?? list.results;
      }
      if (!Array.isArray(list) || list.length === 0) return [];

      const sorted = [...list].sort((a, b) => {
        const pa = phaseSortKey(a);
        const pb = phaseSortKey(b);
        if (pa !== pb) return pa - pb;
        return subPhaseSortKey(a) - subPhaseSortKey(b);
      });

      const phaseNamesInOrder = [];
      const map = new Map();
      for (const item of sorted) {
        const phaseName = item?.process?.phase?.name ?? "Unknown";
        if (!map.has(phaseName)) {
          map.set(phaseName, []);
          phaseNamesInOrder.push(phaseName);
        }
        map.get(phaseName).push({
          is_completed: item.is_completed,
          process_id: item.id,
          name: item?.process?.sub_phase?.name ?? "",
        });
      }

      return phaseNamesInOrder.map((name) => ({
        phaseName: name,
        subPhases: map.get(name),
      }));
    },
    [phaseSortKey, subPhaseSortKey],
  );

  /**
   * Loads checkbox state for every sub-phase on this project (phase accordions).
   * Separate from get-property-details because it is a different backend route.
   */
  const getProcessPropertyStatusByPropertyId = useCallback(
    async (propertyId) => {
      const seq = ++processStatusFetchSeq.current;
      setProcessStatusLoading(true);
      try {
        const allProcessesRes = await getProcessPropertyStatusById(propertyId);
        if (allProcessesRes.error_code === 2 || allProcessesRes.error_code === 3) {
          dispatch(openModal(<ResponseModal text={allProcessesRes.error_message} isError={true} />));
          setProcessesData([]);
          setStatusesData([]);
        } else {
          const groupedData = groupByPhase(allProcessesRes.res.data);
          setProcessesData(groupedData);
          initializeStatusesData(groupedData, propertyId);
        }
      } catch (error) {
        console.error("Error fetching Processes :", error);
        setProcessesData([]);
        setStatusesData([]);
      } finally {
        if (seq === processStatusFetchSeq.current) {
          setProcessStatusLoading(false);
        }
      }
    },
    [dispatch, groupByPhase, initializeStatusesData],
  );

  // === Getting Contacts Data ===
  const fetchAllContacts = useCallback(async () => {
    if (!canViewContactsSection) {
      setAllContacts([]);
      return;
    }
    setContactsLoading(true);
    try {
      const allContactsRes = await getAllContacts();
      if (allContactsRes.error_code === 2 || allContactsRes.error_code === 3) {
        dispatch(openModal(<ResponseModal text={allContactsRes.error_message} isError={true} />));
        setAllContacts([]);
      } else {
        setAllContacts(allContactsRes.res.data || []);
      }
    } catch (error) {
      console.error("Error fetching contacts:", error);
    } finally {
      setContactsLoading(false);
    }
  }, [canViewContactsSection, dispatch]);

  // === Getting Companies Data ===
  const fetchAllCompanies = useCallback(async () => {
    if (!canViewCompaniesSection) {
      setAllCompanies([]);
      return;
    }
    setCompaniesLoading(true);
    try {
      const allCompaniesRes = await getAllCompanies();
      if (allCompaniesRes.error_code === 2 || allCompaniesRes.error_code === 3) {
        dispatch(openModal(<ResponseModal text={allCompaniesRes.error_message} isError={true} />));
        setAllCompanies([]);
      } else {
        setAllCompanies(allCompaniesRes.res.data || []);
      }
    } catch (error) {
      console.error("Error fetching companies:", error);
    } finally {
      setCompaniesLoading(false);
    }
  }, [canViewCompaniesSection, dispatch]);

  useEffect(() => {
    if (reduxSelectedPropertyId !== resolvedPropertyId) return;
    if (!propertyImages || typeof propertyImages !== "object" || Array.isArray(propertyImages)) {
      return;
    }
    setProfileImage(propertyImages.property_main_picture);
    setImages(propertyImages.property_images);
  }, [propertyImages, reduxSelectedPropertyId, resolvedPropertyId]);

  // useEffect(() => {
  //   setNotes(propertyNotes);
  // }, [propertyNotes]);

  useEffect(() => {
    if (!resolvedPropertyId) {
      dispatch(openModal(<ResponseModal text="No project selected. Open a project from the list." isError={true} />));
      navigate(routeNames.PROPERTIES);
      return;
    }

    let cancelled = false;

    const loadPropertyDetail = async () => {
      const detailsRes = await getPropertyDetails(resolvedPropertyId);
      if (cancelled) return;

      if (detailsRes?.error_code === 2 || detailsRes?.error_code === 3) {
        dispatch(openModal(<ResponseModal text={detailsRes.error_message ?? "Could not load project details."} isError={true} />));
        navigate(routeNames.PROPERTIES);
        return;
      }

      const raw = detailsRes.res?.data;
      const detail = raw != null && typeof raw === "object" && raw.data !== undefined && raw.data !== null ? raw.data : raw;

      if (cancelled || detail == null || (typeof detail === "object" && detail.id == null)) {
        if (!cancelled) {
          dispatch(openModal(<ResponseModal text="Could not load project details." isError={true} />));
          navigate(routeNames.PROPERTIES);
        }
        return;
      }

      if (Number(detail.id) !== Number(resolvedPropertyId)) return;

      setPropertyData(detail);
      setNotes(detail.notes);
      setProfileImage(detail.property_main_picture);
      setImages(detail.property_images?.length > 0 ? detail.property_images : []);
      setAssignedContactIds(new Set((detail.assigned_contacts || []).map((c) => c.id)));
      setAssignedCompanyIds(new Set((detail.assigned_companies || []).map((c) => c.id)));

      setProcessesData([]);
      setStatusesData([]);

      await fetchAllContacts();
      await fetchAllCompanies();
      if (canViewProcessStatuses) {
        await getProcessPropertyStatusByPropertyId(resolvedPropertyId);
      }
    };

    loadPropertyDetail();

    return () => {
      cancelled = true;
    };
  }, [
    canViewCompaniesSection,
    canViewContactsSection,
    canViewProcessStatuses,
    dispatch,
    fetchAllCompanies,
    fetchAllContacts,
    getProcessPropertyStatusByPropertyId,
    location.state,
    navigate,
    resolvedPropertyId,
  ]);

  useEffect(() => {
    if (resolvedPropertyId && searchParams.get("id") !== String(resolvedPropertyId)) {
      navigate(`${routeNames.PROPERTY_DETAILS}?id=${resolvedPropertyId}`, {
        replace: true,
        state: location.state,
      });
    }
  }, [resolvedPropertyId, navigate, searchParams, location.state]);

  // === Handle Checkbox Change ===
  const handleCheckboxChange = (processId, isChecked) => {
    if (!canModifyProcessStatuses) return;
    const updatedStatuses = [...statusesData];
    const index = updatedStatuses.findIndex((item) => item.process_property_status_id === processId);

    if (index >= 0) {
      updatedStatuses[index].is_completed = isChecked;
    } else {
      console.error("Process ID not found in statusesData:", processId);
    }

    setStatusesData(updatedStatuses);
  };

  // === Edit Handlers ===
  const updateStatusesHandler = async (e) => {
    e.preventDefault();
    if (!canModifyProcessStatuses) return;
    const updateStatusesRes = await updateProcessPropertyStatuses(statusesData);
    if (updateStatusesRes.error_code === 1) {
      alert("Statuses Updated Successfully");
      navigate(routeNames.PROPERTIES);
    }
  };

  // === Assign / Unassign Handlers ===
  const handleAssignmentResponse = (res, errorMessage) => {
    if (!res) {
      dispatch(openModal(<ResponseModal text={errorMessage} isError={true} />));
      return false;
    }
    if (res.error_code === 2 || res.error_code === 3) {
      dispatch(openModal(<ResponseModal text={res.error_message || errorMessage} isError={true} />));
      return false;
    }
    return res.error_code === 1;
  };

  const showAssignmentSuccess = (text) => {
    dispatch(openModal(<ResponseModal text={text} isError={false} autoDismissAfterMs={3000} />));
  };

  const handleAssignContact = async (contactId) => {
    if (!canAssignContacts) return;
    const res = await assignPropertyContact({
      property_id: resolvedPropertyId,
      contact_id: contactId,
      user_id: getCookie("userId"),
    });
    if (handleAssignmentResponse(res, "Failed to assign contact")) {
      setAssignedContactIds((prev) => {
        const next = new Set(prev);
        next.add(contactId);
        return next;
      });
      showAssignmentSuccess("Contact assigned successfully.");
    }
  };

  const handleUnassignContact = async (contactId) => {
    if (!canUnassignContacts) return;
    const res = await unassignPropertyContact({
      property_id: resolvedPropertyId,
      contact_id: contactId,
      user_id: getCookie("userId"),
    });
    if (handleAssignmentResponse(res, "Failed to unassign contact")) {
      setAssignedContactIds((prev) => {
        const next = new Set(prev);
        next.delete(contactId);
        return next;
      });
      showAssignmentSuccess("Contact unassigned successfully.");
    }
  };

  const handleAssignCompany = async (companyId) => {
    if (!canAssignCompanies) return;
    const res = await assignPropertyCompany({
      property_id: resolvedPropertyId,
      company_id: companyId,
      user_id: getCookie("userId"),
    });
    if (handleAssignmentResponse(res, "Failed to assign company")) {
      setAssignedCompanyIds((prev) => {
        const next = new Set(prev);
        next.add(companyId);
        return next;
      });
      showAssignmentSuccess("Company assigned successfully.");
    }
  };

  const handleUnassignCompany = async (companyId) => {
    if (!canUnassignCompanies) return;
    const res = await unassignPropertyCompany({
      property_id: resolvedPropertyId,
      company_id: companyId,
      user_id: getCookie("userId"),
    });
    if (handleAssignmentResponse(res, "Failed to unassign company")) {
      setAssignedCompanyIds((prev) => {
        const next = new Set(prev);
        next.delete(companyId);
        return next;
      });
      showAssignmentSuccess("Company unassigned successfully.");
    }
  };

  // === Filtered lists: assigned first (A–Z), then unassigned (A–Z) ===
  const contactDisplayName = (c) => `${c.first_name || ""} ${c.last_name || ""}`.trim().toLowerCase();

  const contactCompanyLabel = (c) => {
    if (c.company && typeof c.company === "object" && c.company.name) {
      return String(c.company.name);
    }
    return "";
  };

  const contactMatchesSearch = (c, q) => {
    if (!q) return true;
    const email = (c.email || "").toLowerCase();
    const first = (c.first_name || "").toLowerCase();
    const last = (c.last_name || "").toLowerCase();
    const fullName = contactDisplayName(c);
    const legacyName = typeof c.name === "string" ? c.name.toLowerCase() : "";

    const haystacks = [fullName, first, last, email, legacyName].filter(Boolean);

    const tokens = q.split(/\s+/).filter(Boolean);
    if (tokens.length <= 1) {
      return haystacks.some((h) => h.includes(tokens[0] ?? q));
    }
    return tokens.every((t) => haystacks.some((h) => h.includes(t)));
  };

  const filteredContacts = allContacts
    .filter((c) => contactMatchesSearch(c, contactSearch.trim().toLowerCase()))
    .sort((a, b) => {
      const aAssigned = assignedContactIds.has(a.id);
      const bAssigned = assignedContactIds.has(b.id);
      if (aAssigned !== bAssigned) return aAssigned ? -1 : 1;
      return contactDisplayName(a).localeCompare(contactDisplayName(b));
    });

  const companyCategoryLabel = (c) => {
    if (c.category && typeof c.category === "object" && c.category.name) {
      return String(c.category.name);
    }
    return "";
  };

  const filteredCompanies = allCompanies
    .filter((c) => {
      const q = companySearch.trim().toLowerCase();
      if (!q) return true;
      const name = (c.name || "").toLowerCase();
      const category = companyCategoryLabel(c).toLowerCase();
      const email = (c.email || "").toLowerCase();
      return name.includes(q) || category.includes(q) || email.includes(q);
    })
    .sort((a, b) => {
      const aAssigned = assignedCompanyIds.has(a.id);
      const bAssigned = assignedCompanyIds.has(b.id);
      if (aAssigned !== bAssigned) return aAssigned ? -1 : 1;
      return (a.name || "").localeCompare(b.name || "", undefined, {
        sensitivity: "base",
      });
    });

  const contactTeamRows = useMemo(
    () => filteredContacts.map((contact) => mapContactTeamMemberRow(contact, contactCompanyLabel)),
    [filteredContacts],
  );

  const companyTeamRows = useMemo(
    () => filteredCompanies.map((company) => mapCompanyTeamMemberRow(company, companyCategoryLabel)),
    [filteredCompanies],
  );

  const contactTeamColumns = useMemo(
    () =>
      buildContactTeamMemberColumns({
        isAdminRole: isAdminUser,
        assignedContactIds,
        canAssignContacts,
        canUnassignContacts,
        onAssign: handleAssignContact,
        onUnassign: handleUnassignContact,
      }),
    [isAdminUser, assignedContactIds, canAssignContacts, canUnassignContacts],
  );

  const companyTeamColumns = useMemo(
    () =>
      buildCompanyTeamMemberColumns({
        assignedCompanyIds,
        canAssignCompanies,
        canUnassignCompanies,
        onAssign: handleAssignCompany,
        onUnassign: handleUnassignCompany,
      }),
    [assignedCompanyIds, canAssignCompanies, canUnassignCompanies],
  );

  const contactTableClassName = isAdminUser
    ? "team-members-table--contacts team-members-table--contacts-admin"
    : "team-members-table--contacts";

  const deleteImage = (img) => {
    if (!canModifyPropertyImageDeletes) return;
    const propertyImageId = img?.id ?? img?.property_image_id;
    if (propertyImageId == null) return;
    dispatch(
      deletePropertyImages({
        propertyImageId,
        propertyId: resolvedPropertyId,
      }),
    );
  };

  const createImages = () => {
    return (images || []).map((img, index) => (
      <div className="property-details-carousel-image-card" key={img?.id ?? img?.property_image_id ?? index}>
        {canModifyPropertyImageDeletes && (
          <div
            className="property-details-carousel-delete-wrap"
            onPointerDown={(e) => e.stopPropagation()}
            onClick={(e) => e.stopPropagation()}
          >
            <Button
              className="delete-image-btn property-details-carousel-delete-btn"
              buttonText={<img src={trash} alt="Delete" />}
              onClick={() => deleteImage(img)}
            />
          </div>
        )}
        <div className="property-details-carousel-image-frame">
          <img
            className="property-details-carousel-img"
            src={img?.image}
            alt="Property"
            key={`${resolvedPropertyId}-${img?.id ?? img?.property_image_id ?? img?.image ?? index}`}
          />
        </div>
      </div>
    ));
  };

  const handleImageChange = (e, type) => {
    if (!canModifyPropertyImages) return;
    const payload = {
      imageFlag: null,
      images: null,
      propertyId: resolvedPropertyId,
    };

    switch (type) {
      case "single":
        payload.imageFlag = "profile-image";
        payload.images = e.target.files[0];
        break;

      case "mutiple":
        const arrayOfObjects = Object.keys(e.target.files).map((key) => e.target.files[key]);
        payload.imageFlag = "property-images";
        payload.images = arrayOfObjects;
        break;

      default:
        break;
    }

    dispatch(updatePropertyImages(payload));
  };

  const handleUpdateNotes = () => {
    if (!canModifyPropertyNotes) return;
    dispatch(
      updatePropertyNotes({
        notes,
        propertyId: resolvedPropertyId,
      }),
    );
  };

  if (!resolvedPropertyId) {
    return null;
  }

  if (!propertyData) {
    return (
      <>
        <div className="main-container-propertyDetails">
          <div className="section-main-container-propertyDetails">
            <p className="property-details-loading">Loading project…</p>
          </div>
        </div>
      </>
    );
  }

  const data = propertyData;
  const projectTypeLabel =
    typeof data.type === "string"
      ? data.type
      : data.type?.name ?? data.type_name ?? "—";

  return (
    <>
      <div className="main-container-propertyDetails">
        <div className="section-main-container-propertyDetails">
          {/* 1st Row Property primary info */}
          <div className="section-row-container-propertyDetails">
            <div className="section-container-propertyDetails">
              <div className="section-container-propertyDetails-bankInfo">
                <div className="property-details-summary-inner">
                  <h3>
                    {" "}
                    {data.street_number} {data.street_name} {data.city} {data.state} {data.zip_code}
                  </h3>
                  <div className="label-text-container">
                    <p>
                      {" "}
                      <strong>Start Date:</strong> {data.start_date}
                    </p>
                  </div>
                  <div className="label-text-container">
                    <p>
                      {" "}
                      <strong>Close Date:</strong> {data.close_date}
                    </p>
                  </div>
                  <div className="label-text-container">
                    <p>
                      {" "}
                      <strong>Project Type:</strong> {projectTypeLabel}
                    </p>
                  </div>
                  <div className="label-text-container">
                    <p>
                      {" "}
                      <strong>Project Status:</strong> {data.status}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
          {/* Team Members + Companies */}
          {(canViewContactsSection || canViewCompaniesSection) && (
            <div className="section-row-container-propertyDetails">
              <div className="section-container-propertyDetails">
                <div className="section-container-propertyDetails-bankInfo team-companies-container">
                  {/* Team Members (Contacts) */}
                  {canViewContactsSection && (
                    <div className="team-companies-column">
                      <h3>Team Members</h3>
                      <TextField
                        className="team-companies-search"
                        name="contactSearch"
                        placeHolder="Search by name or email"
                        value={contactSearch}
                        setValue={setContactSearch}
                      />
                      <div className="team-companies-scroll-list team-members-table-scroll">
                        {contactsLoading ? (
                          <div className="team-companies-spinner-wrap">
                            <CircularProgress size={36} thickness={4} />
                          </div>
                        ) : contactTeamRows.length > 0 ? (
                          <TeamMembersTable columns={contactTeamColumns} rows={contactTeamRows} tableClassName={contactTableClassName} />
                        ) : (
                          <div className="team-companies-empty">No contacts found</div>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Companies */}
                  {canViewCompaniesSection && (
                    <div className="team-companies-column">
                      <h3>Companies</h3>
                      <TextField
                        className="team-companies-search"
                        name="companySearch"
                        placeHolder="Search by name, category, or email"
                        value={companySearch}
                        setValue={setCompanySearch}
                      />
                      <div className="team-companies-scroll-list team-members-table-scroll">
                        {companiesLoading ? (
                          <div className="team-companies-spinner-wrap">
                            <CircularProgress size={36} thickness={4} />
                          </div>
                        ) : companyTeamRows.length > 0 ? (
                          <TeamMembersTable
                            columns={companyTeamColumns}
                            rows={companyTeamRows}
                            tableClassName="team-members-table--companies"
                          />
                        ) : (
                          <div className="team-companies-empty">No companies found</div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
          {/* Profile picture + Project Notes */}
          <div className="section-row-container-propertyDetails">
            <div className="section-container-propertyDetails">
              <div className="section-container-propertyDetails-bankInfo property-details-profile-notes-layout">
                <div className="property-details-profile-notes-top">
                  <div className="property-details-profile-side">
                    <h3 className="property-details-profile-notes-heading">
                      <strong> Project Profile Picture:</strong>
                    </h3>
                    <div className="property-details-profile-side-body">
                      <div className="column-propertyInfo-profile-picture">
                        <img key={resolvedPropertyId} src={profileImage ? profileImage : myImage} alt="Property" />
                      </div>
                    </div>
                  </div>
                  <div className="property-details-notes-side column-propertyInfo">
                    <h3 className="property-details-profile-notes-heading">Project Notes :</h3>
                    <div className="property-details-notes-side-body">
                      <div className="labeltext-container-propertyNotes">
                        <TextField
                          className="project-notes-textfield"
                          name="notes"
                          type="multilined"
                          disabled={!canModifyPropertyNotes}
                          setValue={setNotes}
                          value={notes}
                        />
                      </div>
                    </div>
                  </div>
                </div>
                {(canModifyPropertyImages || canModifyPropertyNotes) && (
                  <div className="property-details-profile-notes-actions">
                    {canModifyPropertyImages && (
                      <div className="property-details-profile-notes-actions__cell property-details-profile-notes-actions__cell--profile">
                        <label className="image-btn property-details-profile-notes-action-btn">
                          <input type="file" id="propertyImage" accept="image/*,.pdf" onChange={(e) => handleImageChange(e, "single")} />
                          Edit Image
                        </label>
                      </div>
                    )}
                    {canModifyPropertyNotes && (
                      <div className="property-details-profile-notes-actions__cell property-details-profile-notes-actions__cell--notes column-propertyInfo">
                        <Button
                          buttonText={"Edit Notes"}
                          className="btn-login property-details-profile-notes-action-btn"
                          onClick={handleUpdateNotes}
                        />
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
          {/* Property Images */}

          {canViewPropertyImagesSection && (
            <div className="section-row-container-propertyDetails">
              <div className="section-container-propertyDetails">
                <div className="section-container-propertyDetails-bankInfo property-details-photos-layout">
                  <div className="column-propertyInfo property-details-photos-column">
                    <h3 className="property-details-profile-notes-heading property-details-section-heading--photos">
                      <strong> Project Photos:</strong>
                    </h3>
                  </div>
                  <div className="App property-details-carousel property-details-photos-content" style={{ width: "99%" }}>
                    {images?.length > 0 ? (
                      <Carousel items={createImages()} numberOfDeskTopItems={3} centerMode={false} />
                    ) : (
                      <div className="no-images-found">No Images Found</div>
                    )}
                    {canModifyPropertyImages && (
                      <div className="edit-image-container">
                        <label className="image-btn">
                          <input
                            type="file"
                            id="propertyImage"
                            accept="image/png, image/jpeg"
                            multiple
                            onChange={(e) => handleImageChange(e, "mutiple")}
                          />
                          {"Add Images"}
                        </label>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}
          {/* Property facts: address, parcel, spaces, utilities (accordions + 3-col grid) */}
          <div className="section-row-container-propertyDetails">
            <div className="section-container-propertyDetails">
              <div className="section-container-propertyDetails-bankInfo">
                <div className="column-propertyInfo">
                  <h3 className="property-details-profile-notes-heading property-details-section-heading--details">
                    <strong> Project Details:</strong>
                  </h3>
                  <PropertySummaryAccordions data={data} />
                </div>
              </div>
            </div>
          </div>
          {/* Tasks (per-property) — immediately before Project Status */}
          {canViewTasks && (
            <div className="section-row-container-propertyDetails">
              <div className="section-container-propertyDetails">
                <div className="section-container-propertyDetails-bankInfo">
                  <div className="column-propertyInfo">
                    <PropertyTasksSection
                      propertyId={Number(resolvedPropertyId)}
                      propertyTypeId={data.type?.id != null && !Number.isNaN(Number(data.type.id)) ? Number(data.type.id) : null}
                      canCreateTasks={canCreateTasks}
                      canUpdateTasks={canUpdateTasks}
                      canDeleteTasks={canDeleteTasks}
                      dispatch={dispatch}
                    />
                  </div>
                </div>
              </div>
            </div>
          )}
          {/* 4th Row Property status */}
          {canViewProcessStatuses && (
            <div className="section-row-container-propertyDetails">
              <div className="section-container-propertyDetails">
                <div className="section-container-propertyDetails-bankInfo">
                  {/* Property Status */}
                  <div className="column-propertyInfo">
                    <h3 className="property-details-profile-notes-heading property-details-section-heading--status">
                      <strong> Project Status:</strong>
                    </h3>
                    {processStatusLoading ? (
                      <div className="team-companies-spinner-wrap" role="status" aria-live="polite" aria-label="Loading project status">
                        <CircularProgress size={36} thickness={4} />
                      </div>
                    ) : (
                      <ProjectProcessAccordion
                        processesData={processesData}
                        statusesData={statusesData}
                        onCheckboxChange={handleCheckboxChange}
                        checkboxDisabled={!canModifyProcessStatuses}
                      />
                    )}
                    {canModifyProcessStatuses && (
                      <div className="update-button-container update-button-container--project-status">
                        <Button
                          buttonText={"Update Project Status"}
                          className="btn-login property-details-update-project-status-btn"
                          disabled={processStatusLoading}
                          onClick={(e) => updateStatusesHandler(e)}
                        />
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );
}

export default PropertyDetails;
